import React, {CSSProperties, useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {WINDOW_MODE_MOBILE_WIDTH} from 'consts'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {
  getThemeColor,
  showSnackbar,
  useHistory,
  useRoboyuCustomizationHooks,
} from 'utils'
import {ForkygramRecipientData, TreeFototreeCommentResponse} from 'types'
import {Icon, ListLazy, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {useDispatch, useSelector} from 'lib/redux'
import {FototreeGlobalChatProps} from './FototreeGlobalChatProps'
import FototreeGlobalChatInput from './FototreeGlobalChatInput'
import FototreeGlobalChatItem from './FototreeGlobalChatItem'

interface StyledPreviewContainerProps {
  display: CSSProperties['display']
}

const StyledContainer = styled.div`
  position: fixed;
  background-color: ${({theme}) => getThemeColor(theme, 'black', 0.95)};
  height: 100%;
  width: 100%;
  max-width: ${convertUnit(632)};
  z-index: 4;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  overflow: hidden;
`

const StyledPreviewContainer = styled.div<StyledPreviewContainerProps>`
  position: fixed;
  background-color: ${({theme}) => getThemeColor(theme, 'black', 0.5)};
  width: calc(100% - ${convertUnit(40)});
  max-width: calc((2 / 3) * ${convertUnit(596)});
  border-radius: ${convertUnit(8)};
  bottom: ${convertUnit(78)};
  padding: ${convertUnit(4)} 0;
  cursor: pointer;
  display: ${({display}) => display};
  z-index: 1;

  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    width: calc((2 / 3) * 100%);
  }
`

const StyledContentContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
`

const StyledEmptyContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
`

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${convertUnit(10)};
  padding: ${convertUnit(16)} ${convertUnit(18)};
`

const StyledIconContainer = styled.div`
  display: flex;
  justify-content: center;
  width: ${convertUnit(42)};
  height: ${convertUnit(42)};
  cursor: pointer;
`

export default function FototreeGlobalChat({
  stateData,
  stateOverlay,
  userType,
  treeId,
  isUserScroll,
}: FototreeGlobalChatProps) {
  const history = useHistory()
  const user = useSelector('user')
  const {update} = useDispatch()
  const {translate} = useTranslation()
  const {kycStatus} = useSelector('yuserActivationState')
  const stateRecipient = useState<ForkygramRecipientData>({
    comment_id: '',
    username: '',
  })
  const stateNewReply = useState<TreeFototreeCommentResponse>()
  const stateMessage = useState('')
  const stateSearch = useState(0)
  const stateLoading = useState(false)
  const [recipient, setRecipient] = stateRecipient
  const [data, setData] = stateData
  const [overlay, setOverlay] = stateOverlay
  const [message, setMessage] = stateMessage
  const [search, setSearch] = stateSearch
  const setNewReply = stateNewReply[1]
  const setLoading = stateLoading[1]
  const {handleFetchRoboyuCustomizationStatus} = useRoboyuCustomizationHooks()

  const handleLoadData = useCallback(
    async (page: number, limit: number) => {
      if (isUserScroll && overlay === 'hidden') return
      const response = await requestData('tree_get_fototree_comment', {
        useDefaultMessage: true,
        params: {
          page,
          limit,
          tree_id: treeId,
        },
      })
      return typeof response !== 'string' && response.status === 200
        ? response.data.result
        : []
    },
    [isUserScroll, overlay, treeId],
  )

  const handleFeatureDisabled = useCallback(() => {
    if (kycStatus === 'onhold') {
      return history.push('giftshop_similar_identity', {})
    }
    if (kycStatus === 'unregistered') {
      history.push('giftshop_robopet_activation_selfie', {})
      return
    }
    history.push('giftshop_robopet', {})
  }, [history, kycStatus])

  const handleNewReply = useCallback(
    (id: string, parentCommentId: string) => {
      user &&
        setNewReply({
          id,
          comment: message,
          created_at: new Date().toISOString(),
          is_liked: false,
          like_count: 0,
          reply_count: 0,
          parentCommentId,
          member: {
            id: user.id,
            username: user.username,
            photo: user.photo,
            creator_status: user.creator_status,
          },
        })
    },
    [message, setNewReply, user],
  )

  const handleSendMessage = useCallback(() => {
    if (!(kycStatus === 'verified' || kycStatus === 'verified_check')) {
      handleFeatureDisabled()
      return
    }
    setLoading(true)
    if (!recipient.username) {
      requestData('tree_post_fototree_comment', {
        useDefaultMessage: true,
        data: {
          tree_id: treeId,
          comment: message,
        },
        onRequestReceived: () => setLoading(false),
        onRequestSuccess: ({status, data: {detail}}) => {
          if (status === 201) {
            setSearch(new Date().getTime())
            setMessage('')
            update('generalAccountState', {hasLocallyCountedExp: true})
            handleFetchRoboyuCustomizationStatus(true)
          } else if (
            status === 400 &&
            (!!detail?.comment || !!detail?.general_comment)
          ) {
            const raw = detail.comment ?? detail.general_comment ?? ''
            showSnackbar(
              translate('tree:globalChatLimitPerDay', {
                type: translate('global:comment').toLowerCase(),
                context: detail.general_comment && 'general',
                count: Number(raw.match(/\d+/)?.[0]),
              }),
            )
          }
        },
      })
    } else {
      requestData('tree_post_fototree_comment_reply', {
        useDefaultMessage: true,
        data: {
          tree_id: treeId,
          comment_id: recipient.comment_id,
          comment: message,
        },
        onRequestReceived: () => setLoading(false),
        onRequestSuccess: ({status, data: {result, detail}}) => {
          if (status === 201) {
            handleNewReply(result.id, recipient.comment_id)
            setSearch(new Date().getTime())
            setMessage('')
            setRecipient({
              comment_id: '',
              username: '',
            })
            update('generalAccountState', {hasLocallyCountedExp: true})
            handleFetchRoboyuCustomizationStatus(true)
          } else if (
            status === 400 &&
            (!!detail?.reply || !!detail?.general_reply)
          ) {
            const raw = detail.reply ?? detail.general_reply ?? ''
            showSnackbar(
              translate('tree:globalChatLimitPerDay', {
                type: translate('global:reply').toLowerCase(),
                context: detail.general_reply && 'general',
                count: Number(raw.match(/\d+/)?.[0]),
              }),
            )
          }
        },
      })
    }
  }, [
    handleFeatureDisabled,
    handleFetchRoboyuCustomizationStatus,
    handleNewReply,
    kycStatus,
    message,
    recipient.comment_id,
    recipient.username,
    setLoading,
    setMessage,
    setRecipient,
    setSearch,
    translate,
    treeId,
    update,
  ])

  const handleRenderItem = useCallback(
    (item: TreeFototreeCommentResponse) => (
      <FototreeGlobalChatItem
        treeId={treeId}
        data={item}
        overlay={overlay}
        userType={userType}
        stateRecipient={stateRecipient}
        stateSearch={stateSearch}
        stateNewReply={stateNewReply}
      />
    ),
    [overlay, stateNewReply, stateRecipient, stateSearch, treeId, userType],
  )

  const handleRenderList = useMemo(
    () => (
      <ListLazy
        scrollbar={false}
        keyExtractor={(item) => item.id}
        stateData={stateData}
        renderItem={handleRenderItem}
        loadData={handleLoadData}
        search={search}
        listEmptyElement={
          <StyledEmptyContainer>
            <Paragraph color="gray_6">
              {translate('giftShop:emptyDefaultMessage')}
            </Paragraph>
          </StyledEmptyContainer>
        }
      />
    ),
    [handleLoadData, handleRenderItem, search, stateData, translate],
  )

  const handleRenderInputBox = useMemo(
    () => (
      <FototreeGlobalChatInput
        treeId={treeId}
        stateLoading={stateLoading}
        stateMessage={stateMessage}
        stateRecipient={stateRecipient}
        onSend={handleSendMessage}
      />
    ),
    [handleSendMessage, stateLoading, stateMessage, stateRecipient, treeId],
  )

  const handleRenderHeader = useMemo(
    () => (
      <StyledHeader>
        <StyledIconContainer
          onClick={() => setOverlay(isUserScroll ? 'hidden' : 'preview')}>
          <Icon type="back" size={convertUnit(20)} color="white_1" />,
        </StyledIconContainer>
        <Paragraph color="white_1" fontWeight="medium" fontSize="l">
          {translate('tree:globalChat')}
        </Paragraph>
      </StyledHeader>
    ),
    [isUserScroll, setOverlay, translate],
  )

  const handleRenderContent = useMemo(() => {
    switch (overlay) {
      case 'preview':
        return (
          <StyledPreviewContainer
            display={!data.length ? 'none' : undefined}
            onClick={() => {
              setOverlay('full')
              setData([])
            }}>
            {data.slice(0, 2).map((item) => (
              <FototreeGlobalChatItem
                key={item.id}
                data={item}
                treeId={treeId}
                overlay={overlay}
                stateRecipient={stateRecipient}
                stateSearch={stateSearch}
                stateNewReply={stateNewReply}
              />
            ))}
          </StyledPreviewContainer>
        )
      case 'full':
        return (
          <StyledContainer>
            <StyledContentContainer>
              {handleRenderHeader}
              {handleRenderList}
              {handleRenderInputBox}
            </StyledContentContainer>
          </StyledContainer>
        )
      default:
        return handleRenderList
    }
  }, [
    data,
    handleRenderHeader,
    handleRenderInputBox,
    handleRenderList,
    overlay,
    setData,
    setOverlay,
    stateNewReply,
    stateRecipient,
    stateSearch,
    treeId,
  ])

  return handleRenderContent
}
